// Verwaltung › System (Hintergrundjobs, Konsistenzbeweis, Suchindex, Speicher, Stand der Datenbank) und das eigene
// Profil jeder Person (Name, Sprache, Sitzungen).
import { PERMISSION_AUTHENTICATED, defineContract } from '@shared/contract'
import { zId } from '@shared/ids'
import { z } from 'zod'

const zTimestamp = z.string()

export const zJobSummary = z.object({
  name: z.string(),
  /** deutsch: „Audit-Protokoll versiegeln“ */
  title: z.string(),
  /** Zeitplan in Worten („stündlich“, „täglich 03:10“) oder null bei Jobs, die Ereignisse auslösen */
  schedule: z.string().nullable(),
  lastRunAt: zTimestamp.nullable(),
  lastStatus: z.enum(['done', 'failed', 'running', 'pending']).nullable(),
  nextRunAt: zTimestamp.nullable(),
  pending: z.int().nonnegative(),
  failed: z.int().nonnegative(),
})

export const zJobFailure = z.object({
  id: zId,
  name: z.string(),
  title: z.string(),
  failedAt: zTimestamp.nullable(),
  attempts: z.int().nonnegative(),
  error: z.string().nullable(),
  /** lässt sich über „Erneut versuchen“ neu einstellen */
  retryable: z.boolean(),
})

export const systemStatus = defineContract({
  name: 'platform.system.status',
  title: 'Systemzustand anzeigen',
  description:
    'Hintergrundjobs mit letztem und nächstem Lauf, Fehlschläge, Ergebnis des nächtlichen Konsistenzbeweises, Suchindex, Speicherverbrauch und die Module des Betriebs. Den Stand der Datenbank-Migrationen meldet GET /api/health.',
  llm: {
    description:
      'Liefert den Zustand des Systems für diesen Betrieb: Hintergrundjobs (letzter Lauf, Fehlschläge), Ergebnis des ' +
      'nächtlichen Konsistenzbeweises der Bestände, Größe des Suchindex, Speicherverbrauch der Dateien, aktive ' +
      'Module. Verwenden für „Läuft alles?“ oder „Wann lief der Bestandsbeweis zuletzt?“.',
    keywords: ['System', 'Jobs', 'Hintergrund', 'Konsistenz', 'Speicher', 'Version', 'Zustand', 'läuft alles'],
  },
  input: z.strictObject({}),
  output: z.object({
    /** eine Zeile: „Alles in Ordnung“ bzw. was Aufmerksamkeit braucht */
    health: z.object({ ok: z.boolean(), line: z.string(), issues: z.array(z.string()) }),
    jobs: z.array(zJobSummary),
    failures: z.array(zJobFailure),
    ledger: z.object({
      lastRunAt: zTimestamp.nullable(),
      ok: z.boolean().nullable(),
      /** verletzte Invarianten des letzten Laufs, in Worten */
      violations: z.array(z.string()),
      runs: z.int().nonnegative(),
    }),
    search: z.object({ entries: z.int().nonnegative(), byType: z.array(z.object({ type: z.string(), label: z.string(), entries: z.int() })), rebuildPending: z.boolean() }),
    storage: z.object({ files: z.int().nonnegative(), bytes: z.int().nonnegative(), text: z.string() }),
    product: z.object({ name: z.string(), modules: z.array(z.object({ id: z.string(), title: z.string(), active: z.boolean() })) }),
  }),
  permission: 'platform.system.manage',
  risk: 'read',
})

export const systemRetryJob = defineContract({
  name: 'platform.system.retryJob',
  title: 'Gescheiterten Job erneut versuchen',
  description: 'Stellt einen gescheiterten Hintergrundjob mit denselben Angaben neu ein.',
  llm: { description: 'Stellt einen gescheiterten Hintergrundjob neu ein. Nur für die Oberfläche der Verwaltung.', keywords: [] },
  input: z.strictObject({ jobId: zId }),
  output: z.object({ queued: z.boolean() }),
  permission: 'platform.system.manage',
  risk: 'write',
  assistant: 'never',
})

// ---- eigenes Profil ---------------------------------------------------------------------------------------------------

export const PROFILE_LOCALES = ['de-DE'] as const

export const profileGet = defineContract({
  name: 'platform.profile.get',
  title: 'Eigenes Profil anzeigen',
  description: 'Name, E-Mail-Adresse, Sprache, Rollen und die eigenen laufenden Sitzungen.',
  llm: {
    description:
      'Liefert das Profil der angemeldeten Person: Name, E-Mail, Rollen je Standort und laufende Sitzungen. Verwenden für ' +
      '„Mit welcher Rolle bin ich angemeldet?“.',
    keywords: ['Profil', 'mein Konto', 'meine Rolle', 'Sitzung', 'angemeldet'],
  },
  input: z.strictObject({}),
  output: z.object({
    displayName: z.string(),
    email: z.string(),
    locale: z.string(),
    lastLoginAt: zTimestamp.nullable(),
    roles: z.array(z.object({ roleName: z.string(), locationLabel: z.string() })),
    sessions: z.array(
      z.object({
        id: zId,
        createdAt: zTimestamp,
        lastSeenAt: zTimestamp,
        expiresAt: zTimestamp,
        /** jüngste Sitzung dieser Person – sehr wahrscheinlich die, in der sie gerade arbeitet */
        newest: z.boolean(),
      }),
    ),
    /** false: Für diesen Zugang gibt es keine PIN (technischer Zugang) */
    pinAvailable: z.boolean(),
    /** wann die PIN für Freigaben festgelegt wurde – null = keine hinterlegt. Die PIN selbst verlässt den Server nie. */
    pinSetAt: zTimestamp.nullable(),
    /** nach Fehlversuchen gesperrt bis */
    pinLockedUntil: zTimestamp.nullable(),
    /** offene Bitte, das eigene Konto zu löschen (platform.profile.requestDeletion) – null = keine */
    deletionRequestedAt: zTimestamp.nullable(),
  }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'read',
})

/** Art der Aufgabe „Konto löschen“ für die Verwaltung des Betriebs */
export const ACCOUNT_DELETION_TASK = 'platform.account.deletion'

export const profileRequestDeletion = defineContract({
  name: 'platform.profile.requestDeletion',
  title: 'Löschung des eigenen Kontos beantragen',
  description:
    'Bittet die Verwaltung des Betriebs, das eigene Konto zu löschen: Die Inhaber bekommen eine Aufgabe und eine Benachrichtigung. ' +
    'Der Zugang bleibt bestehen, bis die Verwaltung ihn sperrt bzw. löscht; Belege und Protokolleinträge, die der Betrieb aufbewahren muss, bleiben erhalten.',
  llm: { description: 'Beantragt die Löschung des eigenen Kontos. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({
    /** freiwillige Nachricht an die Verwaltung */
    note: z.string().trim().max(500, { error: 'Die Nachricht hat höchstens 500 Zeichen.' }).optional(),
  }),
  output: z.object({
    requestedAt: zTimestamp,
    taskId: zId,
    /** true: Es lag schon eine offene Bitte vor – keine zweite Aufgabe */
    alreadyRequested: z.boolean(),
  }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  assistant: 'never',
})

export const profileUpdate = defineContract({
  name: 'platform.profile.update',
  title: 'Eigenes Profil ändern',
  description: 'Ändert den eigenen Anzeigenamen und die Sprache.',
  llm: { description: 'Ändert das eigene Profil. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({
    displayName: z.string().trim().min(2, { error: 'Bitte geben Sie Ihren Namen ein.' }).max(120).optional(),
    locale: z.enum(PROFILE_LOCALES).optional(),
  }),
  output: z.object({ displayName: z.string(), locale: z.string() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  assistant: 'never',
})

export const profileSetPin = defineContract({
  name: 'platform.profile.setPin',
  title: 'PIN für Freigaben festlegen',
  description:
    'Legt die eigene PIN fest (4 bis 8 Ziffern) oder ersetzt sie. Mit ihr geben Sie an einem fremden Gerät frei, was die angemeldete Person nicht allein darf – etwa einen höheren Rabatt an der Kasse.',
  llm: { description: 'Legt die eigene PIN fest. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({
    pin: z.string().min(4, { error: 'Die PIN braucht mindestens 4 Ziffern.' }).max(8, { error: 'Die PIN hat höchstens 8 Ziffern.' }),
  }),
  output: z.object({ pinSetAt: zTimestamp.nullable() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  assistant: 'never',
})

export const profileClearPin = defineContract({
  name: 'platform.profile.clearPin',
  title: 'PIN für Freigaben entfernen',
  description: 'Entfernt die eigene PIN. Freigaben an fremden Geräten sind danach nicht mehr möglich, bis Sie eine neue festlegen.',
  llm: { description: 'Entfernt die eigene PIN. Nur für die Oberfläche.', keywords: [] },
  input: z.strictObject({}),
  output: z.object({ cleared: z.boolean() }),
  permission: PERMISSION_AUTHENTICATED,
  risk: 'write',
  assistant: 'never',
})
